import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { formatPhoneForLinks } from '../../../utility/phoneHelper'
import { formatMembershipStatusDisplay } from '../../../utility/enrollmentsBilling'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'

import { ReactComponent as ChevronIcon } from '../../../assets/images/common/icons/arrowDown.svg'
import { ReactComponent as CopyIcon } from '../../../assets/images/common/icons/copy-icon.svg'
import { ReactComponent as BillingCheckCircle } from '../../../assets/images/common/icons/billing-check-circle.svg'

import classes from './EnrollmentDetailDrawer.module.scss'

// The drawer's student header: avatar, name and a link to the member, plus inline approve/decline
// while the enrollment is pending. The family admin's contact details expand from here rather than
// living in their own tab, so the coach can read them without leaving the enrollment.
const EnrollmentStudentHeader = ({ student, displayStatus, actionLoading, onApprove, onDecline }) => {
  const { t } = useTranslation()
  const consumerPlan = useSelector(state => state.user.current_user.communities[0].license_type === 'consumer_plan')
  const [contactOpen, setContactOpen] = useState(false)

  const familyAdmin = student.familyAdmins?.[0]

  const handleCopy = (e, text) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    toast.success(t('enrollmentsBilling.drawer.copiedEmail'), { position: toast.POSITION.TOP_RIGHT })
  }

  const phone = familyAdmin?.phone ? [familyAdmin.phonePrefix, familyAdmin.phone].filter(Boolean).join(' ') : null

  return (
    <div className={classes.studentHeader}>
      <div className={classes.studentRow}>
        <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
        <div className={classes.studentInfo}>
          <h6 className='mb-0 font-weight-bold'>{student.firstName} {student.lastName}</h6>
          <Link
            className={classes.viewMemberLink}
            to={`/profile/${student.id}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('enrollmentsBilling.drawer.viewMember')}
          </Link>
        </div>

        <div className={classes.headerActions}>
          {displayStatus === 'pending_approval' && (
            <>
              <button className={classes.approveButton} onClick={onApprove} disabled={actionLoading}>
                {t('enrollmentsBilling.drawer.approve')}
              </button>
              <button className={classes.declineButton} onClick={onDecline} disabled={actionLoading}>
                {t('enrollmentsBilling.drawer.decline')}
              </button>
            </>
          )}
          {/* Nothing to expand when the student has no family admin on record. */}
          {familyAdmin && (
            <button
              className={classes.contactToggle}
              onClick={() => setContactOpen(open => !open)}
              aria-expanded={contactOpen}
            >
              {contactOpen ? t('enrollmentsBilling.drawer.hideContactInfo') : t('enrollmentsBilling.drawer.contactInfo')}
              <ChevronIcon
                width={14}
                height={14}
                className={classNames(classes.contactChevron, { [classes.contactChevronUp]: contactOpen })}
              />
            </button>
          )}
          {consumerPlan && student.membershipActive !== undefined && (
            <span className={classNames(classes.membershipBadge, { [classes.membershipBadgeActive]: student.membershipActive })}>
              <BillingCheckCircle className={classes.membershipBadgeIcon} width={19} height={19} />
              {formatMembershipStatusDisplay(student.membershipActive, t).label}
            </span>
          )}
        </div>
      </div>

      {contactOpen && familyAdmin && (
        <div className={classes.contactPanel}>
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.familyAdmin')}</span>
            <span className={classes.detailValue}>{familyAdmin.firstName} {familyAdmin.lastName}</span>
          </div>
          {familyAdmin.email && (
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.contactEmail')}</span>
              {/* Reads as the design's plain value, but stays a mailto: - the tab this replaced had
                  the email and phone as links, and coaches use them to reach the parent. */}
              <span className={classes.emailWrapper}>
                <a className={classNames(classes.detailValue, classes.contactLink)} href={`mailto:${familyAdmin.email}`}>
                  {familyAdmin.email}
                </a>
                <button
                  onClick={(e) => handleCopy(e, familyAdmin.email)}
                  className={classes.copyButton}
                  aria-label={t('enrollmentsBilling.drawer.copyEmailAddress')}
                >
                  <CopyIcon className={classes.copyIcon} />
                </button>
              </span>
            </div>
          )}
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.contactPhone')}</span>
            {phone ? (
              <a
                className={classNames(classes.detailValue, classes.contactLink)}
                href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}
              >
                {phone}
              </a>
            ) : (
              <span className={classes.detailValue}>{t('enrollmentsBilling.drawer.notAvailable')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

EnrollmentStudentHeader.propTypes = {
  student: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  actionLoading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
}

export default EnrollmentStudentHeader
